import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(isoWeek);

// Dakar est en UTC+0 toute l'année (pas d'heure d'été) : on force tous les
// calculs en UTC pour ne pas dépendre du fuseau du navigateur qui affiche la page.
export const toDakar = (dateStr) => (dateStr ? dayjs.utc(dateStr) : null);

export const SEGMENT_GRADES = [
  "CI",
  "CP",
  "CE1",
  "CE2",
  "CM1",
  "CM2",
  "Collège",
  "Lycée",
  "Préscolaire",
];

const SEGMENT_SLUGS = {
  CI: "ci",
  CP: "cp",
  CE1: "ce1",
  CE2: "ce2",
  CM1: "cm1",
  CM2: "cm2",
  Collège: "college",
  Lycée: "lycee",
  Préscolaire: "prescolaire",
};

export const SEGMENT_ORDER = [
  "ci",
  "cp",
  "ce1",
  "ce2",
  "cm1",
  "cm2",
  "college",
  "lycee",
  "prescolaire",
  "non_renseigne",
];

export const SEGMENT_LABELS = {
  ci: "CI",
  cp: "CP",
  ce1: "CE1",
  ce2: "CE2",
  cm1: "CM1",
  cm2: "CM2",
  college: "Collège",
  lycee: "Lycée",
  prescolaire: "Préscolaire",
  non_renseigne: "Non renseigné",
};

// Segment = niveau du premier enfant enregistré. Approximation : on utilise
// le niveau ACTUEL de cet enfant (la classe au moment précis de son ajout
// n'est pas conservée si l'enfant a changé de classe depuis).
export function detectSegment(className) {
  if (!className) return "non_renseigne";
  const upper = className.toUpperCase();
  for (const grade of SEGMENT_GRADES) {
    if (upper.includes(grade.toUpperCase())) return SEGMENT_SLUGS[grade];
  }
  return "non_renseigne";
}

// Lundi 00:00 (Dakar) de la semaine ISO contenant `at`.
export function weekMondayOf(at) {
  return at.startOf("isoWeek");
}

function weekSundayEndOf(weekMonday) {
  return weekMonday.add(6, "day").endOf("day");
}

const median = (values) => {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

/**
 * Calcule une ligne de la table de cohortes (une semaine × un segment).
 *
 * `parents` : [{ t0: dayjs, activities: [{ date: dayjs }] }]
 * `now`     : dayjs — date de calcul (sert à déterminer les échéances)
 *
 * Règle : une colonne dont l'échéance n'est pas encore atteinte pour TOUS
 * les parents de la cohorte reste `null` (jamais 0).
 */
export function computeCohortRow({ weekMonday, segment, parents, now }) {
  const weekEnd = weekSundayEndOf(weekMonday);
  const deadlinePassedInDays = (days) => now.diff(weekEnd, "day") >= days;

  const withinDays = (p, days) =>
    p.activities.filter((a) => {
      const diffH = a.date.diff(p.t0, "hour");
      return diffH >= 0 && diffH <= days * 24;
    });

  // Semaine relative N = [T0 + 7(N-1)j ; T0 + 7Nj[
  const withinRelativeWeek = (p, weekIndex) => {
    const startH = 7 * (weekIndex - 1) * 24;
    const endH = 7 * weekIndex * 24;
    return p.activities.filter((a) => {
      const diffH = a.date.diff(p.t0, "hour");
      return diffH >= startH && diffH < endH;
    });
  };

  const cell = (deadlineDays, compute) =>
    deadlinePassedInDays(deadlineDays) ? compute() : null;

  const n_j1 = cell(1, () =>
    parents.filter((p) => withinDays(p, 1).length >= 1).length,
  );
  const n_j3 = cell(3, () =>
    parents.filter((p) => withinDays(p, 3).length >= 1).length,
  );
  const n_j7 = cell(7, () =>
    parents.filter((p) => withinDays(p, 7).length >= 1).length,
  );

  const delai_median_1re_activite = cell(7, () => {
    const delaysInHours = parents
      .map((p) => {
        if (!p.activities.length) return null;
        const first = p.activities.reduce(
          (min, a) => (a.date.isBefore(min) ? a.date : min),
          p.activities[0].date,
        );
        return first.diff(p.t0, "hour");
      })
      .filter((v) => v !== null);
    return median(delaysInHours);
  });

  const n_act2_j7 = cell(7, () =>
    parents.filter((p) => withinDays(p, 7).length >= 2).length,
  );
  const n_act2_j30 = cell(30, () =>
    parents.filter((p) => withinDays(p, 30).length >= 2).length,
  );

  const n_sem1_act = cell(7, () =>
    parents.filter((p) => withinRelativeWeek(p, 1).length >= 1).length,
  );
  const n_sem2_act = cell(14, () =>
    parents.filter((p) => withinRelativeWeek(p, 2).length >= 1).length,
  );
  const n_sem3_act = cell(21, () =>
    parents.filter((p) => withinRelativeWeek(p, 3).length >= 1).length,
  );
  const n_sem4_act = cell(28, () =>
    parents.filter((p) => withinRelativeWeek(p, 4).length >= 1).length,
  );

  const n_engage_j30 = cell(30, () =>
    parents.filter((p) => withinDays(p, 30).length >= 8).length,
  );
  const n_habitude_j30 = cell(28, () =>
    parents.filter((p) =>
      [1, 2, 3, 4].every((w) => withinRelativeWeek(p, w).length >= 2),
    ).length,
  );

  return {
    semaine_inscription: weekMonday.format("YYYY-MM-DD"),
    segment,
    n_prospects: parents.length,
    date_calcul: now.toISOString(),
    n_j1,
    n_j3,
    n_j7,
    delai_median_1re_activite,
    n_act2_j7,
    n_act2_j30,
    n_sem1_act,
    n_sem2_act,
    n_sem3_act,
    n_sem4_act,
    n_engage_j30,
    n_habitude_j30,
  };
}

// Exécute `worker` sur chaque item avec un nombre limité d'appels en
// parallèle (évite d'envoyer des centaines de requêtes simultanées qui
// figent le navigateur).
export async function mapWithConcurrency(
  items,
  worker,
  concurrency,
  onProgress,
  isCancelled,
) {
  const results = new Array(items.length);
  let index = 0;
  let completed = 0;

  const run = async () => {
    while (index < items.length) {
      if (isCancelled?.()) return;
      const current = index++;
      try {
        results[current] = await worker(items[current], current);
      } catch (err) {
        results[current] = null;
      }
      completed++;
      onProgress?.(completed, items.length);
    }
  };

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    run,
  );
  await Promise.all(workers);
  return results;
}
